import { inject, Injectable } from "@angular/core";
import {
  AccountMarketplace,
  AlgoMode,
  CampaignType,
  ProductGroup,
  SDStrategy,
  SPStrategy,
  Strategy,
  StrategyAsin,
  StrategyStateEnum,
  StrategyType,
} from "@front/m19-api-client";
import { CampaignTypeWithTacos, TacosStrategyUpdateParams } from "@front/m19-models";
import { AccountSelectionService, StrategyCache, TacosStrategy } from "@front/m19-services";
import { Utils } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { CsvExportService, CsvField, fieldExtractor, simpleField } from "@m19-board/services/csv-export.service";
import { map, switchMap, take, withLatestFrom } from "rxjs";

/**
 * This type:
 * - map a string token found in a CSV to an attribute of Strategy object
 * - implement the conversion logic (from string to the attribute type - and vice-versa)
 *
 * We should have parse(csvField.value(strategy)) == strategy[field]
 *
 * csvField.header is the csv header (in camel case)
 * title is a human readable header
 * placeholder is a human-readable example for the user
 */
export type BulkCsvField<T, K extends keyof T, V extends T[K]> = {
  field: K;
  title: string;
  placeHolder?: string;
  parse: (input: string) => V;
  csvField: CsvField<T>;
  readonly: boolean;
};

function stringField<T, K extends keyof T & string>(field: K, readonly?: boolean): BulkCsvField<T, K, T[K]> {
  const title = Utils.camelCaseToHuman(Utils.toCamelCase(field));
  return {
    field,
    title,
    parse: (input) => input as T[K],
    csvField: simpleField<T>(field),
    readonly: readonly ?? false,
  };
}

function booleanField<T, K extends keyof T & string>(
  field: K,
  trueLabel: string,
  falseLabel: string,
  header?: string,
  readonly?: boolean,
): BulkCsvField<T, K, T[K]> {
  const csvHeader = header ?? Utils.toCamelCase(field);
  const title = Utils.camelCaseToHuman(csvHeader);
  return {
    field,
    title,
    parse: (value) => parseBoolean(trueLabel)(value) as T[K],
    csvField: fieldExtractor(csvHeader, (strat) => (strat[field] ? trueLabel : falseLabel)),
    readonly: readonly ?? false,
  };
}

function enumField<T, K extends keyof T & string, V extends object>(
  field: K,
  enumType: V,
  header?: string,
  readonly?: boolean,
): BulkCsvField<T, K, T[K]> {
  const csvHeader = header ?? Utils.toCamelCase(field);
  const title = Utils.camelCaseToHuman(csvHeader);
  return {
    field,
    title,
    placeHolder: Object.keys(enumType).join("|"),
    parse: (input) => ((enumType as any)[input] ? input : undefined) as T[K],
    csvField: simpleField<T>(field, csvHeader),
    readonly: readonly ?? false,
  };
}

function numericField<T, K extends keyof T & string>(
  field: K,
  type: "float" | "int",
  header?: string,
  readonly?: boolean,
): BulkCsvField<T, K, T[K]> {
  const csvHeader = header ?? Utils.toCamelCase(field);
  const title = Utils.camelCaseToHuman(csvHeader);
  const parseFunction = (value: string) => {
    const parsed = type == "float" ? Number.parseFloat(value) : Number.parseInt(value);
    return (Number.isNaN(parsed) ? undefined : parsed) as T[K];
  };
  return {
    field,
    title,
    parse: parseFunction,
    csvField: fieldExtractor(csvHeader, (strat) => (strat[field] as number)?.toFixed(type == "float" ? 2 : 0) ?? "-"),
    readonly: readonly ?? false,
  };
}

function parseBoolean(trueLabel: string) {
  return (value: string) => value == trueLabel;
}

const StrategyAsinsField: BulkCsvField<SPStrategy | SDStrategy, "asins", StrategyAsin[]> = {
  field: "asins",
  title: "ASINs",
  placeHolder: "ASIN1;ASIN2;ASIN3",
  parse: (input: string) =>
    input
      .split(";")
      .map((x) => ({ asin: x.trim() }))
      .filter((a) => Utils.isValidAsin(a.asin)),
  csvField: fieldExtractor("StrategyAsins", (strat) =>
    !strat.asins || strat.asins.length == 0 ? "-" : strat.asins.map((a) => a.asin).join(";"),
  ),
  readonly: false,
};

export type TacosStrategyExport = {
  tacosStrategyGroupId?: number;
  name: string;
  algoMode: AlgoMode;
  asins: string[];
  tacosTarget: number;
  state: StrategyStateEnum;
  campaignTypes?: CampaignType[];
};
const TacosStrategyIdField = numericField<TacosStrategyExport, keyof TacosStrategyExport>(
  "tacosStrategyGroupId",
  "int",
);
const TacosStrategyStateField = enumField<TacosStrategyExport, keyof TacosStrategyExport, typeof StrategyStateEnum>(
  "state",
  StrategyStateEnum,
  "Status",
  true,
);
const TacosAlgoModeField = enumField<TacosStrategyExport, keyof TacosStrategyExport, typeof AlgoMode>(
  "algoMode",
  AlgoMode,
  "Algorithm",
);
const TacosTargetField = numericField<TacosStrategyExport, keyof TacosStrategyExport>("tacosTarget", "float");
const TacosStrategyAsinsField: BulkCsvField<TacosStrategyExport, "asins", string[]> = {
  field: "asins",
  title: "ASINs",
  placeHolder: "ASIN1;ASIN2;ASIN3",
  parse: (input: string) => input.split(";"),
  csvField: fieldExtractor("StrategyAsins", (strat) => strat.asins.join(";")),
  readonly: false,
};
const TacosStrategyNameField = stringField<TacosStrategyExport, keyof TacosStrategyExport>("name");
const TacosCampaignTypesField: BulkCsvField<TacosStrategyExport, "campaignTypes", CampaignType[]> = {
  field: "campaignTypes",
  title: "CampaignTypes",
  parse: (input: string) => input.split(";") as CampaignType[],
  csvField: fieldExtractor("CampaignTypes", (strat) => strat.campaignTypes?.join(";") ?? ""),
  readonly: true,
};

const StrategyIdField = numericField<Strategy, "strategyId">("strategyId", "int");
const StrategyNameField = stringField<Strategy, "name">("name");
const StrategyGroupField = numericField<Strategy, "strategyGroupId">("strategyGroupId", "int", "StrategyGroupId", true);
const StrategyTypeField = enumField<Strategy, "strategyType", typeof StrategyType>(
  "strategyType",
  StrategyType,
  "Type",
  true,
);

const StateField = enumField<Strategy, "state", typeof StrategyStateEnum>("state", StrategyStateEnum, "Status");
const AlgoModeField = enumField<Strategy, "algoMode", typeof AlgoMode>("algoMode", AlgoMode, "Algorithm");
const AcosTargetField = numericField<Strategy, "acosTarget">("acosTarget", "float");
const SuggestedBidField = numericField<Strategy, "suggestedBid">("suggestedBid", "float");
const DailyBudgetField = numericField<Strategy, "dailyBudget">("dailyBudget", "float");
const MinDailySpentField = numericField<Strategy, "minDailySpend">("minDailySpend", "float", "MinDailySpent");
const MonthlyBudgetField = numericField<Strategy, "monthlyBudget">("monthlyBudget", "float");
const NextMonthlyBudgetField = numericField<Strategy, "nextMonthlyBudget">("nextMonthlyBudget", "float");
const AutoAlgoExplorationField = booleanField<Strategy, "disableOtherQueries">(
  "disableOtherQueries",
  "OFF",
  "ON",
  "AutoAlgoExploration",
);
const AutoTargetCampaignField = booleanField<Strategy, "disableAutoSegment">(
  "disableAutoSegment",
  "OFF",
  "ON",
  "AutoTargetCampaign",
);
const ProductTargetingField = booleanField<Strategy, "disableProductSegment">(
  "disableProductSegment",
  "OFF",
  "ON",
  "ProductTargeting",
);
const ProductGroupIdField = numericField<ProductGroup, "productGroupId">("productGroupId", "int");
const ProductGroupName = stringField<ProductGroup, "productGroupName">("productGroupName");
const BoostAudienceField: BulkCsvField<Strategy, "boostAudienceId", string> = {
  field: "boostAudienceId",
  title: "BoostAudienceId",
  parse: (input: string) => (input === "-" ? undefined : input) as string,
  csvField: fieldExtractor("BoostAudienceId", (strat) => strat.boostAudienceId ?? "-"),
  readonly: false,
};

const ProductGroupAsinsField: BulkCsvField<ProductGroup, "items", string[]> = {
  field: "items",
  title: "ASINs",
  placeHolder: "ASIN1;ASIN2;ASIN3",
  parse: (input: string) =>
    input
      .split(";")
      .map((x) => x.trim())
      .filter((a) => Utils.isValidAsin(a)),
  csvField: fieldExtractor("ASINs", (pg) => (!pg.items || pg.items.length == 0 ? "-" : pg.items.join(";"))),
  readonly: false,
};
const BoostActivationField = booleanField<Strategy & { activatePrimeDayBoost: boolean }, "activatePrimeDayBoost">(
  "activatePrimeDayBoost",
  "Yes",
  "No",
  "PromoDaysBoost",
);
const BoostAcosOptimizer = numericField<Strategy & { activatePrimeDayBoost: boolean }, "primeDayBoost">(
  "primeDayBoost",
  "int",
  "ACOSOptimizer",
);

export type ProductGroupUploadResult = {
  created: ProductGroup[];
  updated: ProductGroup[];
  errors: string[];
};

export type ProductGroupBulkOperations = {
  bulkData: string;
  creations: ProductGroupCreation[];
  updates: ProductGroupUpdate[];
  errors: BulkError[];
};

export type BulkError = {
  lineIndex: number;
  csvRow: string;
  error: string;
};

export type ProductGroupUpdate = {
  lineIndex: number;
  csvRow: string;
  productGroupToUpdate: ProductGroup;
  updatedFields: Partial<ProductGroup> & {
    asinsToAdd: string[];
    asinsToDelete: string[];
    asins: string[];
  };
};

export type ProductGroupCreation = {
  lineIndex: number;
  csvRow: string;
  productGroup: ProductGroup;
};

export enum BulkUploadError {
  WRONG_LINE_FORMAT,
  STRATEGY_ID_NOT_AN_INTEGER,
  STATE_NOT_VALID,
  STRATEGY_TYPE_NOT_VALID,
  ASIN_NOT_IN_CATALOG,
  SP_CREATION_NOT_ALLOWED,
  STRATEGY_LIMIT_REACHED,
  STRATEGY_DOES_NOT_EXIST,
  DUPLICATED_STRATEGY_UPDATE,
  NOTHING_TO_UPDATE,
  ASIN_ALREADY_IN_STRATEGY_GROUP,
  ASIN_IN_USE_BY_OTHER_STRATEGY,
  ASIN_NOT_IN_STRATEGY_GROUP,
  PRODUCT_GROUP_ID_NOT_AN_INTEGER,
  CANNOT_HAVE_MORE_THAN_MAX_PRODUCT_GROUP_ITEMS,
  PRODUCT_GROUP_DOES_NOT_EXIST,
  PRODUCT_GROUP_NAME_NOT_VALID,
  INVALID_BOOST_AUDIENCE_ID,
}

@Injectable()
export class BulkImportService {
  private csvExportService = inject(CsvExportService);
  private strategyCache = inject(StrategyCache);
  private accountSelectionService = inject(AccountSelectionService);

  private readonly translocoService = inject(TranslocoService);

  public readonly FMT_BULK_UPLOAD_ERRORS: { [key in BulkUploadError]: string } = {
    [BulkUploadError.WRONG_LINE_FORMAT]: this.translocoService.translate("bulk-upload-error.wrong_line_format"),
    [BulkUploadError.STRATEGY_ID_NOT_AN_INTEGER]: this.translocoService.translate(
      "bulk-upload-error.strategy_id_not_an_integer",
    ),
    [BulkUploadError.STATE_NOT_VALID]: this.translocoService.translate("bulk-upload-error.state_not_valid"),
    [BulkUploadError.STRATEGY_TYPE_NOT_VALID]: this.translocoService.translate(
      "bulk-upload-error.strategy_type_not_valid",
    ),
    [BulkUploadError.ASIN_NOT_IN_CATALOG]: this.translocoService.translate("bulk-upload-error.asin_not_in_catalog"),
    [BulkUploadError.SP_CREATION_NOT_ALLOWED]: this.translocoService.translate(
      "bulk-upload-error.sp_creation_not_allowed",
    ),
    [BulkUploadError.STRATEGY_LIMIT_REACHED]: this.translocoService.translate(
      "bulk-upload-error.strategy_limit_reached",
    ),
    [BulkUploadError.STRATEGY_DOES_NOT_EXIST]: this.translocoService.translate(
      "bulk-upload-error.strategy_does_not_exist",
    ),
    [BulkUploadError.DUPLICATED_STRATEGY_UPDATE]: this.translocoService.translate(
      "bulk-upload-error.duplicated_strategy_update",
    ),
    [BulkUploadError.NOTHING_TO_UPDATE]: this.translocoService.translate("bulk-upload-error.nothing_to_update"),
    [BulkUploadError.ASIN_ALREADY_IN_STRATEGY_GROUP]: this.translocoService.translate(
      "bulk-upload-error.asin_already_in_strategy_group",
    ),
    [BulkUploadError.ASIN_IN_USE_BY_OTHER_STRATEGY]: this.translocoService.translate(
      "bulk-upload-error.asin_in_use_by_other_strategy",
    ),
    [BulkUploadError.ASIN_NOT_IN_STRATEGY_GROUP]: this.translocoService.translate(
      "bulk-upload-error.asin_not_in_strategy_group",
    ),
    [BulkUploadError.PRODUCT_GROUP_ID_NOT_AN_INTEGER]: this.translocoService.translate(
      "bulk-upload-error.product_group_id_not_an_integer",
    ),
    [BulkUploadError.PRODUCT_GROUP_DOES_NOT_EXIST]: this.translocoService.translate(
      "bulk-upload-error.product_group_does_not_exist",
    ),
    [BulkUploadError.PRODUCT_GROUP_NAME_NOT_VALID]: this.translocoService.translate(
      "bulk-upload-error.product_group_name_not_valid",
    ),
    [BulkUploadError.CANNOT_HAVE_MORE_THAN_MAX_PRODUCT_GROUP_ITEMS]: this.translocoService.translate(
      "bulk-upload-error.cannot_have_more_than_max_product_group_items",
    ),
    [BulkUploadError.INVALID_BOOST_AUDIENCE_ID]: this.translocoService.translate(
      "bulk-upload-error.invalid_boost_audience_id",
    ),
  };

  public readonly SpStrategyBulkConfig = [
    StrategyIdField,
    StrategyNameField,
    StrategyGroupField,
    StrategyTypeField,
    StateField,
    AlgoModeField,
    AcosTargetField,
    SuggestedBidField,
    DailyBudgetField,
    MinDailySpentField,
    MonthlyBudgetField,
    NextMonthlyBudgetField,
    AutoAlgoExplorationField,
    AutoTargetCampaignField,
    ProductTargetingField,
    BoostAudienceField,
    StrategyAsinsField,
  ];

  public readonly TacosStrategyBulkConfig = [
    TacosStrategyIdField,
    TacosStrategyNameField,
    TacosStrategyStateField,
    TacosAlgoModeField,
    TacosTargetField,
    TacosStrategyAsinsField,
  ];

  public readonly SdStrategyBulkConfig = [
    StrategyIdField,
    StrategyNameField,
    StateField,
    AlgoModeField,
    AcosTargetField,
    SuggestedBidField,
    DailyBudgetField,
    MinDailySpentField,
    MonthlyBudgetField,
    NextMonthlyBudgetField,
    AutoAlgoExplorationField,
    StrategyAsinsField,
  ];

  public readonly SbStrategyBulkConfig = [
    StrategyIdField,
    StrategyNameField,
    StateField,
    AlgoModeField,
    AcosTargetField,
    SuggestedBidField,
    DailyBudgetField,
    MinDailySpentField,
    MonthlyBudgetField,
    NextMonthlyBudgetField,
    AutoAlgoExplorationField,
  ];

  public readonly ProductGroupBulkConfig = [ProductGroupIdField, ProductGroupName, ProductGroupAsinsField];

  public readonly PromoDaysBoostBulkConfig = [
    StrategyIdField,
    StrategyNameField,
    BoostActivationField,
    BoostAcosOptimizer,
  ];

  public readonly ProductGroupBulkConfigDescription: { field: string; description: string }[] = [
    { field: "ProductGroupId", description: "0 to create, existing id to update" },
    { field: "ProductGroupName", description: "string" },
    { field: "Items", description: "list of ASINs, separated by semicolons (;)" },
  ];

  public exportTacosStrategyCsv(accountMarketplace: AccountMarketplace, strategies: TacosStrategyExport[]) {
    const fields = this.TacosStrategyBulkConfig.map((f) => f.csvField).concat(TacosCampaignTypesField.csvField);

    this.csvExportService.exportCsv(
      {
        prefix: `tacos_strategy_list`,
        marketplace: accountMarketplace.marketplace,
        accountGroupName: accountMarketplace.accountGroupName,
        fieldSeparator: "\t",
        quoteStrings: "",
      },
      strategies,
      fields,
    );
  }

  public exportStrategyCsv(accountMarketplace: AccountMarketplace, campaignType: CampaignType, strategies: Strategy[]) {
    const fields = this.getBulkConfig(campaignType).map((f) => f.csvField);

    this.csvExportService.exportCsv(
      {
        prefix: `${campaignType}_strategy_list`,
        marketplace: accountMarketplace.marketplace,
        accountGroupName: accountMarketplace.accountGroupName,
        fieldSeparator: "\t",
        quoteStrings: "",
      },
      strategies,
      fields,
    );
  }

  public exportProductGroupCsv(accountMarketplace: AccountMarketplace, productGroups: ProductGroup[]) {
    this.csvExportService.exportCsv(
      {
        prefix: `product_group_list`,
        marketplace: accountMarketplace.marketplace,
        accountGroupName: accountMarketplace.accountGroupName,
        fieldSeparator: "\t",
        quoteStrings: "",
      },
      productGroups,
      this.ProductGroupBulkConfig.map((f) => f.csvField),
    );
  }

  public exportPromoDaysBoostCsv() {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        take(1),
        switchMap((am) => this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace)),
        map((index) =>
          Array.from(index.values())
            .filter(
              (s) =>
                s.campaignType !== CampaignType.DSP &&
                !s.isTacos() &&
                s.state !== StrategyStateEnum.PAUSED &&
                s.algoMode === AlgoMode.ACOS_TARGET &&
                !s.dailyBudget,
            )
            .map((s) => ({
              ...s,
              activatePrimeDayBoost: s.primeDayBoost !== undefined,
            })),
        ),
        withLatestFrom(this.accountSelectionService.singleAccountMarketplaceSelection$),
      )
      .subscribe(([strategies, am]) => {
        this.csvExportService.exportCsv(
          {
            prefix: `promo_days`,
            marketplace: am.marketplace,
            accountGroupName: am.accountGroupName,
            fieldSeparator: "\t",
            quoteStrings: "",
          },
          strategies,
          this.PromoDaysBoostBulkConfig.map((f) => f.csvField),
        );
      });
  }

  public getSdStrategyBulkConfig() {
    return this.SdStrategyBulkConfig;
  }

  public getBulkConfig(campaignType: CampaignTypeWithTacos) {
    if (campaignType == CampaignType.SP) {
      return this.SpStrategyBulkConfig;
    }
    if (campaignType == CampaignType.SD) {
      return this.SdStrategyBulkConfig;
    }
    if (campaignType == CampaignType.SB) {
      return this.SbStrategyBulkConfig;
    }
    throw `Cannot find CSV config for ${campaignType} strategies`;
  }

  public getProductGroupBulkConfigExample(): string {
    return (
      "// Creation\n" + "0	MyProductGroup	ASIN1;ASIN2;...\n" + "// Update\n" + "42	MyExistingProductGroup	ASIN1;ASIN2;..."
    );
  }

  public parseTacosCsvRow(row: string[]): Partial<TacosStrategyExport> | null {
    const config = this.TacosStrategyBulkConfig;
    if (row.length < config.length) {
      return null;
    }
    const result: any = {};
    for (let i = 0; i < config.length; i++) {
      const field = config[i];
      result[field.field] = field.parse(row[i]);
    }
    return result;
  }

  public parseCsvRow<T extends Strategy>(row: string[], campaignType: CampaignType): Partial<T> | null {
    const config = this.getBulkConfig(campaignType);
    if (row.length < config.length) {
      return null;
    }
    const result: any = {};
    for (let i = 0; i < config.length; i++) {
      const field = config[i];
      result[field.field] = field.parse(row[i]);
    }
    return result;
  }

  public parseProductGroupCsvRow(row: string[]): Partial<ProductGroup> | null {
    const config = this.ProductGroupBulkConfig;
    if (row.length < config.length) {
      return null;
    }
    const result: any = {};
    for (let i = 0; i < config.length; i++) {
      const field = config[i];
      result[field.field] = field.parse(row[i]);
    }
    return result;
  }

  public parseStrategyBoostCsvRow(row: string[]): Partial<Strategy & { activatePrimeDayBoost: string }> | null {
    const config = this.PromoDaysBoostBulkConfig;
    if (row.length < config.length) {
      return null;
    }
    const result: any = {};
    for (let i = 0; i < config.length; i++) {
      const field = config[i];
      result[field.field] = field.parse(row[i]);
    }
    return result;
  }

  public getTacosStrategyUpdateParams(
    change: Partial<TacosStrategyExport>,
    originalStrategy: TacosStrategy,
    organizationId: number,
  ): TacosStrategyUpdateParams {
    const target = { ...originalStrategy, ...change };
    const updateParams: TacosStrategyUpdateParams = {
      tacosStrategyGroupId: originalStrategy.tacosStrategyGroupId!,
      accountId: originalStrategy.accountId!,
      marketplace: originalStrategy.marketplace!,
      organizationId,
      asinsToAdd: [],
      asinsToDelete: [],
    };
    if (target.tacosTarget !== originalStrategy.tacosTarget) {
      updateParams.tacosTarget = target.tacosTarget;
    }
    if (target.state !== originalStrategy.state) {
      updateParams.state = target.state;
    }
    if (target.name !== originalStrategy.spStrategy!.name) {
      updateParams.name = target.name;
    }
    return updateParams;
  }

  public getProductGroupUpdateParams(
    change: Partial<ProductGroup>,
    originalProductGroup: ProductGroup,
  ): Partial<ProductGroup> & {
    asinsToAdd: string[];
    asinsToDelete: string[];
    asins: string[];
  } {
    const config = this.ProductGroupBulkConfig;
    const target = { ...originalProductGroup, ...change };
    const updateParams: Partial<ProductGroup> & {
      asinsToAdd: string[];
      asinsToDelete: string[];
      asins: string[];
    } = { asinsToAdd: [], asinsToDelete: [], asins: [] };
    for (const field of config) {
      if (field.readonly) {
        continue;
      }
      if (field.field == "items") {
        continue;
      }
      if (target[field.field] !== originalProductGroup[field.field]) {
        (updateParams as any)[field.field as string] = target[field.field];
      }
    }
    for (const asin of target.items!) {
      if (!originalProductGroup.items!.find((a) => a === asin) && !updateParams.asinsToAdd.includes(asin)) {
        updateParams.asinsToAdd.push(asin);
      }
      updateParams.asins.push(asin);
    }
    for (const asin of originalProductGroup.items!) {
      if (!target.items!.find((a) => a === asin) && !updateParams.asinsToDelete.includes(asin)) {
        updateParams.asinsToDelete.push(asin);
      }
    }
    return updateParams;
  }
}
